import { useCallback, useMemo } from 'react'
import { defineSteps, step } from 'flows/common/helpers/defineSteps'
import { BrandPlanAwarenessRedesign } from 'components/FirstTimeExperienceModal/steps/BrandPlanAwareness/BrandPlanAwarenessRedesign'
import { BrandPlanTactics } from 'components/FirstTimeExperienceModal/steps/BrandPlanTactics/BrandPlanTactics'
import { BrandPlanTacticsRedesign } from 'components/FirstTimeExperienceModal/steps/BrandPlanTactics/BrandPlanTacticsRedesign'
import { BrandProfileStep } from 'components/FirstTimeExperienceModal/steps/BrandProfile/BrandProfileStep'
import { BrandProfileWebsite } from 'screens/VirtualMarketerOnboarding/BrandProfileWebsite/BrandProfileWebsite'

import { FlowCtx } from 'flows/common/machines/createFlowMachine'
import { StepProps } from 'flows/virtualMarketerOnboarding/Types'
import { CampaignDetails } from 'components/FirstTimeExperienceModal/steps/CampaignGeneration/CampaignDetails'
import {
  CampaignGenerationLoadingScreen,
  CampaignGenerationLoadingProvider,
} from 'components/FirstTimeExperienceModal/steps/CampaignGeneration'
import { useBrandKit } from 'hooks/queries/brandKits/useBrandKit'
import { useDeleteBrandKitSource } from 'hooks/queries/useDeleteBrandKitSource'
import { useDeleteBrandProfile } from 'hooks/queries/brandProfiles/useDeleteBrandProfile'
import { AutopilotSidebar as ProfileAutopilotSidebar } from 'components/FirstTimeExperienceModal/steps/BrandProfile/AutopilotSidebar'
import { ScreenshotFallback } from 'screens/VirtualMarketerOnboarding/ScreenshotFallback/ScreenshotFallback'
import useResponsiveDesign from 'hooks/useResponsiveDesign'
import { NoWebsite } from 'screens/VirtualMarketerOnboarding/NoWebsite/NoWebsite'
import { BrandVisualStyleSelection } from 'screens/VirtualMarketerOnboarding/BrandVisualStyleSelection'
import { BrandVisualStylePreview } from 'screens/VirtualMarketerOnboarding/BrandVisualStylePreview'
import { useFlipperFlag } from 'hooks/useFlipperFlag'
import { BrandVisualStyleFonts } from 'screens/VirtualMarketerOnboarding/BrandVisualStyleFonts/BrandVisualStyleFonts'
import { BrandVisualStyleVoiceAndLogo } from 'screens/VirtualMarketerOnboarding/BrandVisualStyleVoiceAndLogo/BrandVisualStyleVoiceAndLogo'
import { useIsPioneerStarterOrGrowth } from 'hooks/useIsPioneerStarterOrGrowth'
import { useChooseOnboardingPlan } from './useChooseOnboardingPlan'
import { useExperiment } from 'utils/experiments'
import { StrategiesSelection } from 'screens/VirtualMarketerOnboarding/StrategiesSelection/StrategiesSelection'
import { GoalSelection } from 'screens/VirtualMarketerOnboarding/GoalSelection/GoalSelection'

export const useOnboardingStrategiesEnabled = () => {
  const experimentVariant = useExperiment('onboarding_strategies')
  const { isMobile } = useResponsiveDesign()

  return !isMobile && experimentVariant === 'on'
}

const useCleanupBrandProfile = (brandProfileId: number, brandKitId?: number) => {
  const { data: brandKitData } = useBrandKit(brandKitId, { cacheTime: 1000 * 60 * 5 })
  const { mutate: deleteBrandProfile } = useDeleteBrandProfile({ id: brandProfileId })
  const { mutate: deleteBrandKitSource } = useDeleteBrandKitSource()

  const cleanupBrandProfile = useCallback(() => {
    deleteBrandProfile()
    const brandKitSources = brandKitData?.brand_kit?.brand_kit_sources
    brandKitSources?.forEach((source) => {
      brandKitId && deleteBrandKitSource({ brandKitId, id: source.id })
    })
  }, [deleteBrandProfile, brandKitData?.brand_kit?.brand_kit_sources, brandKitId, deleteBrandKitSource])

  return { cleanupBrandProfile }
}

const BrandProfileWebsiteWrapper = ({ send, context }: any) => {
  let effectiveBrandKitId = context.brandKitId
  if (!context.isIntentionalBrandKit) {
    effectiveBrandKitId = null
  }

  return (
    <BrandProfileWebsite
      websites={context.websites}
      language={context.language || context.defaultLanguage}
      brandKitId={effectiveBrandKitId}
      onLanguageChange={(language) => {
        send({ type: 'MERGE_CTX', payload: { language } })
      }}
      onBack={() => send({ type: 'BACK' })}
      onNext={async (brandProfile, brandVisualEnhancementId) => {
        let analysisId = brandProfile.latest_analysis_id

        send({
          type: 'NEXT',
          payload: {
            brandProfileId: brandProfile.id,
            brandKitId: brandProfile.brand_kit.id,
            websiteUrl: brandProfile.url,
            brandPlanId: brandProfile.brand_plan.id,
            analysisId,
            brandVisualEnhancementId,
            websiteScrapeSucceeded: true,
          },
        })
      }}
      onNoWebsite={() => {
        send({ type: 'NO_WEBSITE' })
      }}
    />
  )
}

const ScreenshotFallbackWrapper = ({ send, context }: StepProps) => {
  return (
    <ScreenshotFallback
      onBack={() => send({ type: 'BACK' })}
      brandKitId={context.brandKitId}
      url={context.websiteUrl}
      selectedLanguage={context.selectedLanguage}
      onNext={async (brandProfile) => {
        send({
          type: 'NEXT',
          payload: {
            brandProfileId: brandProfile.id,
            brandKitId: brandProfile.brand_kit.id,
            websiteUrl: brandProfile.url,
            brandPlanId: brandProfile.brand_plan.id,
            analysisId: brandProfile.latest_analysis_id,
            websiteScrapeSucceeded: true,
          },
        })
      }}
    />
  )
}

const NoWebsiteWrapper = ({ send, context }: StepProps) => {
  return (
    <NoWebsite
      onBack={() => send({ type: 'BACK' })}
      brandKitId={context.brandKitId}
      url={context.websiteUrl}
      selectedLanguage={context.selectedLanguage}
      onNext={async (brandProfile) => {
        send({
          type: 'NEXT',
          payload: {
            brandProfileId: brandProfile.id,
            brandKitId: brandProfile.brand_kit.id,
            websiteUrl: brandProfile.url,
            brandPlanId: brandProfile.brand_plan.id,
            analysisId: brandProfile.latest_analysis_id,
          },
        })
      }}
    />
  )
}

const BrandProfileStepWrapper = ({ send, context }: StepProps) => {
  const { cleanupBrandProfile } = useCleanupBrandProfile(context.brandProfileId, context.brandKitId)

  const handleBack = useCallback(
    (websiteScrapeSucceeded = true) => {
      cleanupBrandProfile()

      send({
        type: 'MERGE_CTX',
        payload: {
          brandProfileId: null,
          brandProfile: null,
          skipUploadBrandMaterials: false,
          brandKitId: context.isIntentionalBrandKit ? context.brandKitId : null,
          isIntentionalBrandKit: context.isIntentionalBrandKit || false,
          profile: null,
          websiteScrapeSucceeded,
        },
      })

      send({ type: 'BACK' })
    },
    [cleanupBrandProfile, context.brandKitId, context.isIntentionalBrandKit, send],
  )

  const { saveSubscriptionPlan } = useChooseOnboardingPlan()

  return (
    <BrandProfileStep
      analysisId={context.analysisId}
      id={context.brandProfileId}
      initialProfile={context.profile}
      deleteOnBack={false}
      onAnalysis={(analysisId) => {
        send({ type: 'MERGE_CTX', payload: { analysisId } })
      }}
      onNext={(profile) => {
        saveSubscriptionPlan()

        send({
          type: 'MERGE_CTX',
          payload: { profile },
        })
        send({ type: 'NEXT' })
      }}
      onBack={handleBack}
    />
  )
}

const BrandPlanAwarenessWrapper = ({ send, context, onProvideBackHandler }: StepProps) => {
  const planVariant = useIsPioneerStarterOrGrowth()

  if (!context.brandPlanId) {
    throw new Error('Brand plan ID is required for BrandPlanAwarenessStep')
  }

  const isStarterPlan = planVariant === 'starter'

  const handleNext = (payload?: any) => {
    if (payload?.desktopPath && payload?.publishDay) {
      send({ type: 'MOBILE_COMPLETE', payload })
    } else {
      send({ type: 'NEXT', payload })
    }
  }

  return (
    <BrandPlanAwarenessRedesign
      id={context.brandPlanId}
      selectedBlogPlatform={context.selectedBlogPlatform}
      selectedEmailPlatform={context.selectedEmailPlatform}
      isStarterPlan={isStarterPlan}
      paidAdsOnboardingParameters={context.paidAdsOnboardingParameters}
      onBack={() => send({ type: 'BACK' })}
      onProvideBackHandler={onProvideBackHandler}
      onNext={handleNext}
    />
  )
}

const WhereToPublishWrapper = ({ send, context, onProvideBackHandler }: StepProps) => {
  const planVariant = useIsPioneerStarterOrGrowth()
  const isOnboardingAddVoicePageFlag = useFlipperFlag('onboarding_add_voice_page')

  if (!context.brandPlanId) {
    throw new Error('Brand plan ID is required for WhereToPublish')
  }

  const mode = planVariant === 'starter' ? 'starter' : 'campaign'

  const handleBack = () => {
    if (isOnboardingAddVoicePageFlag) {
      send({ type: 'BACK_TO_VISUAL_STYLES_VOICE' })
    } else {
      send({ type: 'BACK_TO_FONTS' })
    }
  }

  const handleNext = ({
    tactics,
    startDate,
    selectedBlogPlatform,
    selectedEmailPlatform,
    paidAdsOnboardingParameters,
  }: any) => {
    const payload = { tactics, startDate, selectedBlogPlatform, selectedEmailPlatform, paidAdsOnboardingParameters }

    if (planVariant === 'starter') {
      send({ type: 'NEXT_STARTER', payload })
    } else {
      send({ type: 'NEXT_GROWTH', payload })
    }
  }

  // Starter flow uses the redesigned tactics page
  if (mode === 'starter') {
    return (
      <BrandPlanTacticsRedesign
        id={context.brandPlanId}
        mode={mode}
        tactics={context.tactics || []}
        seoKeywords={context.seoKeywords || []}
        idealCustomers={context.idealCustomers || []}
        onBack={handleBack}
        onProvideBackHandler={onProvideBackHandler}
        onNext={handleNext}
      />
    )
  }

  // Growth/campaigns flow uses the original tactics component
  return (
    <BrandPlanTactics
      id={context.brandPlanId}
      mode={mode}
      externalIntegrations={context.externalIntegrations}
      brandKitId={context.brandKitId}
      tactics={context.tactics}
      seoKeywords={context.seoKeywords}
      idealCustomers={context.idealCustomers}
      initialInsightChannels={context.insightChannels || []}
      onBack={handleBack}
      onProvideBackHandler={onProvideBackHandler}
      onNext={handleNext}
    />
  )
}

const CampaignGenerationLoadingWrapper = ({ send, context }: StepProps) => {
  if (!context.brandPlanId) {
    throw new Error('Brand plan ID is required for CampaignGenerationLoading')
  }

  return (
    <CampaignGenerationLoadingProvider>
      <CampaignGenerationLoadingScreen
        brandPlanId={context.brandPlanId}
        brandProfileId={context.brandProfileId}
        analysisId={context.analysisId}
        campaignData={context.campaignData}
        startDate={context.startDate}
        onComplete={() => send({ type: 'COMPLETE' })}
        onBack={() => send({ type: 'BACK' })}
      />
    </CampaignGenerationLoadingProvider>
  )
}

const CampaignDetailsWrapper = ({ send, context, onProvideBackHandler }: StepProps) => {
  if (!context.brandPlanId || !context.brandKitId) {
    throw new Error('Brand plan ID and brand kit ID are required for CampaignDetails')
  }

  return (
    <CampaignDetails
      brandPlanId={context.brandPlanId}
      brandKitId={context.brandKitId}
      initialValues={context.campaignData}
      paidAdsOnboardingParameters={context.paidAdsOnboardingParameters}
      onBack={() => send({ type: 'BACK' })}
      onProvideBackHandler={onProvideBackHandler}
      onNext={(campaignData) => {
        send({
          type: 'NEXT',
          payload: {
            campaignData: {
              growth_goals_input: campaignData.growth_goals_input,
              key_dates_input: campaignData.key_dates_input,
            },
            startDate: campaignData.startDate,
          },
        })
      }}
    />
  )
}

const BrandVisualStyleSelectionWrapper = ({ send, context }: StepProps) => {
  return (
    <BrandVisualStyleSelection
      brandKitId={context.brandKitId}
      onBack={() => send({ type: 'BACK' })}
      onNext={(fontPairings, stylesGenerated, sourceImage) => {
        send({
          type: 'MERGE_CTX',
          payload: { fontPairings, stylesGenerated, sourceImage },
        })
        send({ type: 'NEXT' })
      }}
    />
  )
}

const GoalSelectionWrapper = ({ send, context }: StepProps) => {
  const { cleanupBrandProfile } = useCleanupBrandProfile(context.brandProfileId, context.brandKitId)

  if (!context.brandPlanId) {
    throw new Error('Brand plan ID is required for GoalSelection')
  }

  return (
    <GoalSelection
      initialSelectedBusinessType={context.selectedBusinessType}
      initialSelectedGoal={context.goal}
      onBack={() => {
        cleanupBrandProfile()
        send({ type: 'BACK' })
      }}
      onNext={(selectedBusinessType, goal, brandPlanStrategies) => {
        send({
          type: 'MERGE_CTX',
          payload: { selectedBusinessType, goal, brandPlanStrategies },
        })
        send({ type: 'NEXT' })
      }}
    />
  )
}

const StrategiesSelectionWrapper = ({ send, context }: StepProps) => {
  const { saveSubscriptionPlan } = useChooseOnboardingPlan()

  if (!context.brandPlanId) {
    throw new Error('Brand plan ID is required for StrategiesSelection')
  }

  return (
    <StrategiesSelection
      brandPlanId={context.brandPlanId}
      initialSelectedStrategies={context.brandPlanStrategies}
      selectedBusinessType={context.selectedBusinessType}
      goal={context.goal}
      onNext={(brandPlanStrategies, planKey) => {
        saveSubscriptionPlan(planKey)

        send({
          type: 'MERGE_CTX',
          payload: { brandPlanStrategies },
        })
        send({ type: 'NEXT' })
      }}
      onBack={() => send({ type: 'BACK' })}
    />
  )
}

const BrandVisualStylePreviewWrapper = ({ send, context, onProvideBackHandler }: StepProps) => {
  return (
    <BrandVisualStylePreview
      onBack={() => send({ type: 'BACK' })}
      onProvideBackHandler={onProvideBackHandler}
      initialSelectedStyle={context.selectedVisualStyle}
      initialStylesGenerated={context.stylesGenerated}
      sourceImage={context.sourceImage}
      brandKitId={context.brandKitId}
      onNext={(selectedVisualStyle, stylesGenerated) => {
        send({
          type: 'MERGE_CTX',
          payload: { selectedVisualStyle, stylesGenerated },
        })
        send({ type: 'NEXT' })
      }}
    />
  )
}

const BrandVisualStyleFontsWrapper = ({ send, context, onProvideBackHandler }: StepProps) => {
  return (
    <BrandVisualStyleFonts
      onBack={() => send({ type: 'BACK' })}
      onProvideBackHandler={onProvideBackHandler}
      brandKitId={context.brandKitId}
      initialFontPairings={context.fontPairings}
      initialFontPairingId={context.selectedFontPairingId}
      selectedStyle={context.selectedVisualStyle}
      onNext={(selectedFontPairingId) => {
        send({
          type: 'MERGE_CTX',
          payload: { selectedFontPairingId },
        })
        send({ type: 'NEXT' })
      }}
    />
  )
}

const BrandVisualStyleVoiceAndLogoWrapper = ({ send, context }: StepProps) => {
  const isOnboardingAddVoicePageFlag = useFlipperFlag('onboarding_add_voice_page')

  if (!isOnboardingAddVoicePageFlag || !context.brandKitId) {
    send({ type: 'NEXT' })
    return null
  }

  return (
    <BrandVisualStyleVoiceAndLogo
      onNext={() => {
        send({ type: 'NEXT' })
      }}
      onBack={() => {
        send({ type: 'BACK' })
      }}
      brandKitId={context.brandKitId}
    />
  )
}

const flowStep = step<FlowCtx>

const steps = [
  flowStep('website', BrandProfileWebsiteWrapper)
    .withExtra((ctx) => ({
      createBrandProfileAnalysis: ctx.createBrandProfileAnalysis,
    }))
    .on('NO_WEBSITE', 'noWebsite')
    .on('NEXT', 'profile'),

  flowStep('screenshotFallback', ScreenshotFallbackWrapper)
    .skipWhen((ctx) => ctx.websiteScrapeSucceeded)
    .back('website'),
  flowStep('noWebsite', NoWebsiteWrapper).back('website'),
  flowStep('profile', BrandProfileStepWrapper)
    .withSiderComponent(ProfileAutopilotSidebar)
    .back('website', (ctx) => ctx.websiteScrapeSucceeded)
    .back('screenshotFallback', (ctx) => !ctx.websiteScrapeSucceeded)
    .on('NEXT', 'brandVisualStyleSelection'),
  flowStep('brandVisualStyleSelection', BrandVisualStyleSelectionWrapper)
    .back('profile')
    .on('NEXT', 'brandVisualStylePreview'),

  flowStep('brandVisualStylePreview', BrandVisualStylePreviewWrapper)
    .back('brandVisualStyleSelection')
    .on('NEXT', 'brandVisualStyleFonts'),

  flowStep('brandVisualStyleFonts', BrandVisualStyleFontsWrapper)
    .back('brandVisualStylePreview')
    .on('NEXT', 'brandVisualStyleVoiceAndLogo'),

  flowStep('brandVisualStyleVoiceAndLogo', BrandVisualStyleVoiceAndLogoWrapper)
    .back('brandVisualStyleFonts')
    .on('NEXT', 'whereToPublish'),

  flowStep('whereToPublish', WhereToPublishWrapper)
    .back('brandVisualStyleVoiceAndLogo')
    .on('BACK_TO_VISUAL_STYLES_VOICE', 'brandVisualStyleVoiceAndLogo')
    .on('BACK_TO_FONTS', 'brandVisualStyleFonts')
    .on('NEXT', 'awareness')
    .on('NEXT_STARTER', 'awareness')
    .on('NEXT_GROWTH', 'campaignDetails'),

  flowStep('campaignDetails', CampaignDetailsWrapper).back('whereToPublish').on('NEXT', 'campaignGenerationLoading'),

  flowStep('awareness', BrandPlanAwarenessWrapper)
    .back('whereToPublish')
    .on('NEXT', 'done')
    .on('MOBILE_COMPLETE', 'done'),

  flowStep('campaignGenerationLoading', CampaignGenerationLoadingWrapper)
    .isFullScreen()
    .on('BACK', 'campaignDetails')
    .on('COMPLETE', 'done'),
]

const stepsWithStrategies = [
  flowStep('website', BrandProfileWebsiteWrapper)
    .withExtra((ctx) => ({
      createBrandProfileAnalysis: ctx.createBrandProfileAnalysis,
    }))
    .on('NEXT', 'goalSelection'),

  flowStep('goalSelection', GoalSelectionWrapper).back('website').on('NEXT', 'strategiesSelection'),
  flowStep('strategiesSelection', StrategiesSelectionWrapper)
    .back('goalSelection')
    .on('NEXT', 'brandVisualStyleSelection'),
  flowStep('brandVisualStyleSelection', BrandVisualStyleSelectionWrapper)
    .back('strategiesSelection')
    .on('NEXT', 'brandVisualStylePreview'),

  flowStep('brandVisualStylePreview', BrandVisualStylePreviewWrapper)
    .back('brandVisualStyleSelection')
    .on('NEXT', 'brandVisualStyleFonts'),

  flowStep('brandVisualStyleFonts', BrandVisualStyleFontsWrapper)
    .back('brandVisualStylePreview')
    .on('NEXT', 'campaignGenerationLoading'),

  flowStep('campaignGenerationLoading', CampaignGenerationLoadingWrapper)
    .isFullScreen()
    .on('BACK', 'brandVisualStyleFonts')
    .on('COMPLETE', 'done'),
]

export const useVirtualMarketerRedesignSteps = () => {
  const isOnboardingStrategiesEnabled = useOnboardingStrategiesEnabled()

  return useMemo(
    () => defineSteps<FlowCtx>(isOnboardingStrategiesEnabled ? stepsWithStrategies : steps),
    [isOnboardingStrategiesEnabled],
  )
}
